"use client";

import Link from "next/link";
import Image from "next/image";
import { Images } from "@/assests";
import { motion, useReducedMotion } from "@/lib/motion";
import { ArrowRight } from "@/components/icons";
import PostArticleHeader from "@/components/ui/post-article-header";
import { HeroGlow } from "@/components/ui";
import type { PartnerContent } from "./types";
import { StatsBlock, PullQuote, HighlightCards, BenefitsGrid, RequirementsList } from "./parts";

interface Props {
  partner: PartnerContent;
}

export type { PartnerContent };

export default function PartnerPostSection({ partner }: Props) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <main id="main-content" tabIndex={-1} className="relative overflow-y-clip">
      <HeroGlow align="left" />

      <PostArticleHeader
        backHref="/partners"
        backLabel="Partners"
        current=""
        categoryLabel={partner.typeLabel}
        accentColor={partner.accentColor}
        meta={[]}
        headline={partner.headline}
        tags={[]}
      />

      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <motion.article
          initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="prose prose-invert max-w-none"
          style={{
            ["--tw-prose-body" as string]: "var(--muted-foreground)",
            ["--tw-prose-headings" as string]: "var(--foreground)",
          }}
        >
          <div className="border-foreground/6 mb-6 overflow-hidden rounded-2xl border sm:mb-10">
            <Image
              src={Images.blogHeroWave}
              alt={partner.name}
              width={1200}
              height={480}
              sizes="(min-width: 1024px) 800px, 100vw"
              className="h-auto w-full object-cover"
              priority
            />
          </div>

          <p className="text-muted-foreground mb-6 text-base leading-relaxed font-medium sm:mb-8 sm:text-lg">
            {partner.intro}
          </p>

          {partner.sections.map((section) => (
            <div key={section.id}>
              <h2
                id={section.id}
                className="text-foreground mt-8 mb-3 scroll-mt-24 text-xl font-bold sm:mt-12 sm:mb-4 sm:text-2xl"
              >
                {section.heading}
              </h2>
              {section.body.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 40)}
                  className="text-muted-foreground mb-4 leading-relaxed"
                >
                  {paragraph}
                </p>
              ))}
              {section.stats && <StatsBlock stats={section.stats} />}
              {section.highlights && <HighlightCards highlights={section.highlights} />}
              {section.quote && (
                <PullQuote text={section.quote.text} attribution={section.quote.attribution} />
              )}
            </div>
          ))}

          <BenefitsGrid benefits={partner.benefits} />

          {partner.requirements?.length ? (
            <>
              <h2 className="text-foreground mt-8 mb-3 text-xl font-bold sm:mt-12 sm:mb-4 sm:text-2xl">
                Who is this program for?
              </h2>
              <RequirementsList requirements={partner.requirements} />
            </>
          ) : null}
        </motion.article>
      </div>

      <div className="mx-auto max-w-4xl px-4 pb-16 sm:px-6 sm:pb-24 lg:px-8">
        <div
          className="border-border flex flex-col items-center justify-between gap-6 rounded-2xl border p-8 sm:flex-row sm:p-10"
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--foreground) 3%, transparent) 0%, var(--background) 60%, rgba(16,185,129,0.12) 100%)",
          }}
        >
          <div>
            <h2 className="text-foreground mb-1 text-xl font-bold sm:text-2xl">
              Ready to join the {partner.name}?
            </h2>
            <p className="text-muted-foreground text-sm">
              Apply today and start growing your business with Proxidize.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-3">
            <Link
              href="/partners#partner-form-heading"
              className="text-foreground border-border hover:border-foreground/20 inline-flex items-center gap-2 rounded-lg border px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              Become a Partner
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
